import { useQuery, type QueryClient } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

// Mirrors inrange_agents.cost_summary.build_cost_summary on the BE.
// Backend route is ``POST /cost/summary``. One grouped query over
// agents.run, priced in Python via the single-source pricing module.
// All amounts are USD.

export interface CostSummaryDay {
  date: string; // YYYY-MM-DD
  cost_usd: number;
  runs: number;
  /** The day's spend per platform; sums to cost_usd. */
  by_platform?: Record<string, number>;
}

export interface CostSummaryByPlatform {
  platform: string;
  cost_usd: number;
}

export interface CostSummaryByModel {
  model: string;
  cost_usd: number;
}

export interface CostSummaryByMode {
  mode: string;
  cost_usd: number;
}

export interface CostSummaryByProject {
  project_id: string;
  cost_usd: number;
  // Set when this project has been deleted: "<name> (deleted)". The BE stamps it
  // on the ledger rows at delete time (project_id itself is re-pointed to a dead
  // key), so the by-project breakdown can still name the spend. null for live
  // projects, whose name is resolved FE-side from the live projects list.
  deleted_project_label?: string | null;
}

// A cost source the BE prices, matching the BE's ``kind`` discriminator on
// cost line items: an agent run, a procurement assessment, a document
// extraction, a global file's read and summary, a conversation turn, a gmail-sync NL
// call, or the Bedrock admin health probe. ``unattributed`` is spend captured
// outside any scope (should be rare; every model surface opens one).
export type CostKind =
  | "agent_run"
  | "procurement"
  | "extraction"
  | "global_data"
  | "clause_lists"
  | "conversation"
  | "stateful_agent_run"
  | "agent_daily_sweep"
  | "gmail_sync"
  | "mailbox_sweep"
  | "mailbox_confidentiality"
  | "slack_sync"
  | "admin_health"
  | "operational"
  | "unattributed";

export interface CostSummaryByKind {
  kind: CostKind;
  cost_usd: number;
}

// Spend split by AI-invocation outcome - "succeeded" vs "failed" - so the
// aggregate cost of failed invocations is readable at a glance. Omits a class
// with no spend in the window (an all-clean window lists only "succeeded").
export interface CostSummaryByStatus {
  status: "succeeded" | "failed";
  cost_usd: number;
}

// New: web search (AgentCore Gateway) is billed per query with NO tokens, so it
// is aggregated as its own dimension - the number of searches + their USD cost -
// rather than folded into the token breakdowns. The cost is already included in
// ``total_usd`` / ``this_week_usd`` / ``days``; this surfaces the count and the
// isolated dollar figure beside the model spend. Optional so an older cached
// payload without it doesn't crash the page.
export interface CostSummaryWebSearches {
  count: number;
  cost_usd: number;
}

// Amazon Transcribe (Slack voice notes) bills per second of audio with no tokens,
// so like web search it is its own dimension, already included in the totals.
export interface CostSummaryTranscription {
  seconds: number;
  cost_usd: number;
}

// Agent-run spend per config slot ("default", "sonnet-specialists:cold"); spend
// outside an agent run has no config and is left out.
export interface CostSummaryByConfig {
  config: string;
  cost_usd: number;
}

export interface CostSummary {
  days: CostSummaryDay[];
  by_config?: CostSummaryByConfig[];
  by_model: CostSummaryByModel[];
  by_platform?: CostSummaryByPlatform[];
  by_mode: CostSummaryByMode[];
  by_project: CostSummaryByProject[];
  web_searches?: CostSummaryWebSearches;
  transcription?: CostSummaryTranscription;
  // New: spend split by cost source (agent run / procurement / extraction /
  // global data), descending by cost.
  by_kind: CostSummaryByKind[];
  // New: spend split by outcome (succeeded / failed), descending by cost.
  by_status: CostSummaryByStatus[];
  total_usd: number;
  this_week_usd: number;
  weekly_budget_usd: number;
  // New: how many priced-window calls the BE could NOT price (model not in
  // the price table) and the distinct unknown model ids behind them. These
  // calls are excluded from ``total_usd``, so the FE warns to avoid a silent
  // under-count.
  unpriced_count: number;
  unknown_models: string[];
}

const QUERY_KEY = QK.costSummary;

// Two mutually-exclusive request shapes, mirroring the BE route:
//   { days: N }                       -> trailing N-day window (default 30)
//   { start: "YYYY-MM-DD", end: ... } -> explicit inclusive calendar range
// The explicit range powers the "Spend over time" modal; the trailing
// window backs the Operations Spend tile + the headline total.
export type CostSummaryParams =
  | { days?: number; start?: undefined; end?: undefined }
  | { start: string; end: string; days?: undefined };

export const useCostSummary = (
  params?: CostSummaryParams,
  options?: { enabled?: boolean },
) => {
  const http = useAgentsHttp();
  const days = params && "days" in params ? params.days : undefined;
  const start = params && "start" in params ? params.start : undefined;
  const end = params && "end" in params ? params.end : undefined;
  // An explicit range requires both ends; until the user has picked both
  // we don't fire the request - otherwise a half-set range would fall through
  // to an empty body that the BE treats as the default 30-day window, so the
  // chart would briefly show 30 days of data mid-range-edit. A days/no-args
  // caller is always well-formed and fires normally.
  const hasRange = Boolean(start && end);
  const rangeIntended = start !== undefined || end !== undefined;
  return useQuery<CostSummary>(
    [QUERY_KEY, start ?? null, end ?? null, days ?? null],
    async () =>
      http<CostSummary>(
        "/cost/summary",
        hasRange ? { start, end } : days ? { days } : {},
      ),
    {
      enabled: (!rangeIntended || hasRange) && (options?.enabled ?? true),
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  );
};

// Every spend surface: the /cost/summary rollup (the trailing-30d AI spend badge +
// the /costs page chart/breakdowns) AND the /cost/line-items list. Call this
// wherever spend is created or re-attributed, so no spend surface goes stale.
export const invalidateCost = (qc: QueryClient) => {
  qc.invalidateQueries({ queryKey: [QK.costSummary] });
  qc.invalidateQueries({ queryKey: [QK.costLineItems] });
};
